import { Config } from './config';
import style from './page.module.css';
import { useState, useEffect } from 'react';
import { useCoinStore } from '../store/coinstore';
import { useAuthStore } from '../store/authstore';
import { addCoinData, fetchCoinData } from '../utils/coinApi';
import { useToastStore } from '../store/localapi';
import {
    getCurrentServerTime,
    calculateTimeDiff,
    formatCountdown,
    addHoursToDate,
    isAfterTime,
    formatToMySQLDateTime
} from '../utils/serverTime';
import RewardRule from '../component/coinpopup/RewardRule';


export default function Streak({ values, onAward }: { values: Config; onAward?: (amount: number) => void }) {
    const user = useAuthStore((state) => state.user);
    const coins = useCoinStore((state) => state.coinBalance);
    const { showToast } = useToastStore();
    const [countdown, setCountdown] = useState<string>('');
    const [isEligible, setIsEligible] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isExpired, setIsExpired] = useState<boolean>(false);
    const [showInfo, setShowInfo] = useState<boolean>(false);

    const streakDays = parseInt(coins.streakDays) || 0;
    const weekNumber = streakDays > 0 ? Math.floor((streakDays - 1) / 7) + 1 : 1;
    const coinsPerDay = weekNumber * values.baseCheckinAmount;

    const COOLDOWN_MS = 24 * 60 * 60 * 1000;  // 24 hours (86400000ms)
    const EXPIRY_MS = 48 * 60 * 60 * 1000;    // 48 hours (172800000ms)
    // const COOLDOWN_MS = 10000;  // 24 hours (86400000ms)
    // const EXPIRY_MS = 20000;    // 48 hours (172800000ms)

    const addMs = (dateStr: string, ms: number) => {
        const base = dateStr ? new Date(dateStr) : getCurrentServerTime();
        return new Date(base.getTime() + ms).toISOString();
    };

    // Check eligibility and update countdown
    useEffect(() => {
        if (!user) return;

        const updateStatus = async () => {
            // DEBUG: Log coin state
            // console.log('Streak Debug:', {
            //     lastCheckinTime: coins.lastCheckinTime,
            //     nextEligibleTime: coins.nextEligibleTime,
            //     streakExpiryTime: coins.streakExpiryTime,
            //     streakDays: coins.streakDays
            // });
            
            // If user has never checked in (streakDays === 0) or no active times, user is eligible
            if (!coins.lastCheckinTime || coins.streakDays === "0") {
                setIsEligible(true);
                setIsExpired(false);
                setCountdown('');
                return;
            }

            // Check if streak expired (48hrs passed)
            if (coins.streakExpiryTime && isAfterTime(coins.streakExpiryTime)) {
                // Silently reset streak to 0 on server and locally
                if (streakDays !== 0) {
                    try {
                        // Sync expiry to server with MySQL datetime format
                        await addCoinData(
                            {
                                type: "add",
                                mobile_dt: formatToMySQLDateTime(getCurrentServerTime())
                            },
                            'streakExpiry'
                        );
                        // Reset local state
                        useCoinStore.getState().setCoinBalance({
                            streakDays: "0",
                            weekNumber: 1,
                            lastCheckinTime: '',
                            nextEligibleTime: '',
                            streakExpiryTime: ''
                        });
                    } catch (error) {
                        console.error('Failed to sync expiry:', error);
                    }
                }
                setIsExpired(true);
                setIsEligible(true); // User can check in immediately to restart
                setCountdown('');
                return;
            }

            // Check if eligible (24hrs passed)
            if (coins.nextEligibleTime && isAfterTime(coins.nextEligibleTime)) {
                setIsEligible(true);
                setIsExpired(false);
                setCountdown('');
                return;
            }

            // Still in cooldown, show countdown
            // IMPORTANT: Only show countdown if user has an active streak (streakDays > 0)
            if (coins.streakDays !== "0" && coins.lastCheckinTime && coins.nextEligibleTime) {
                const remainingMs = calculateTimeDiff(coins.nextEligibleTime);
                if (remainingMs > 0) {
                    setCountdown(formatCountdown(remainingMs));
                    setIsEligible(false);
                    setIsExpired(false);
                } else {
                    setIsEligible(true);
                    setCountdown('');
                    setIsExpired(false);
                }
            }
        };

        updateStatus();
        const interval = setInterval(updateStatus, 1000);
        return () => clearInterval(interval);
    }, [user, coins.lastCheckinTime, coins.nextEligibleTime, coins.streakExpiryTime, streakDays]);

    const handleCheckIn = async () => {
        if (!user || isLoading || !isEligible) return;

        setIsLoading(true);
        try {
            // Use current local streak value (already synced from server on login)
            const currentStreakDays = streakDays;
            let newStreakDays = currentStreakDays;

            if (isExpired || currentStreakDays === 0) {
                // Reset streak to day 1
                newStreakDays = 1;
            } else {
                // Increment streak
                newStreakDays = currentStreakDays + 1;
            }

            // Calculate coins for the NEW streak day
            const newWeekNumber = Math.floor((newStreakDays - 1) / 7) + 1;
            const coinsToAward = newWeekNumber * values.baseCheckinAmount;

            // Format time in MySQL datetime format (YYYY-MM-DD HH:MM:SS)
            const currentServerTime = formatToMySQLDateTime(getCurrentServerTime());

            // Call check-in API
            const response = await addCoinData(
                {
                    type: "add",
                    day_streak: newStreakDays.toString(),
                    coins: coinsToAward,
                    mobile_dt: currentServerTime
                },
                'streak'
            );

            if (response && response.ErrorCode === 1) {
                // Use the mobile_dt returned from API (already in correct format)
                const checkinTime = response.mobile_dt || currentServerTime;
                const nextEligible = addMs(checkinTime, COOLDOWN_MS);
                const expiryTime = addMs(checkinTime, EXPIRY_MS);

                // Update coin store
                useCoinStore.getState().setCoinBalance({
                    totalCoins: coins.totalCoins + coinsToAward,
                    streakDays: newStreakDays.toString(),
                    weekNumber: newWeekNumber,
                    lastCheckinTime: checkinTime,
                    nextEligibleTime: nextEligible,
                    streakExpiryTime: expiryTime,
                    serverTime: checkinTime
                });
                window.scrollTo(0, 100);
                showToast(`Checked in! +${coinsToAward} coins • Day ${newStreakDays}`, 'success');
                onAward?.(coinsToAward);
                setIsEligible(false);
                setIsExpired(false);
            } else {
                showToast('Check-in failed. Please try again.', 'error');
            }
        } catch (error) {
            console.error('Check-in error:', error);
            showToast('Check-in failed. Please try again.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className={style.dfx_Check_In}>
                <div className={style.dfx_Check_CoinsAndDays}>
                    <p className={style.dfx_CheckStreak}>Check-In Streak:<span className={style.dfx_Check_Day}>{streakDays}</span>
                        <span>{streakDays <= 1 ? 'Day' : 'Days'}</span>
                        <span className={style.dfx_Check_Day} onClick={() => setShowInfo(true)} >
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                <path d="M8.47144 0C3.79309 0 0 3.79309 0 8.47144C0 13.1509 3.79309 16.9429 8.47144 16.9429C13.1498 16.9429 16.9429 13.1509 16.9429 8.47144C16.9429 3.79309 13.1498 0 8.47144 0ZM9.23652 12.0877L9.3906 12.3688C9.3906 12.3688 8.70864 13.7057 7.56712 13.7057C6.96247 13.7057 6.65961 13.3198 6.65961 12.5483C6.65961 11.9961 6.80575 11.2125 7.09642 10.2007C7.38657 9.18622 7.5327 8.51486 7.5327 8.18394C7.5327 7.88691 7.39345 7.68678 7.11337 7.58406L7.19914 7.18378C7.19914 7.18378 8.19189 6.44624 8.93526 6.44624C9.46048 6.44624 9.7231 6.73056 9.7231 7.30133C9.7231 7.57559 9.60132 8.17124 9.35512 9.09039C9.15551 9.82688 9.00461 10.4294 8.90243 10.8959C8.81666 11.2787 8.7743 11.5413 8.7743 11.6848C8.7743 11.9532 8.92837 12.0877 9.23652 12.0877ZM9.12004 5.56468C8.47674 5.56468 7.95522 5.0458 7.95522 4.4025C7.95522 3.7592 8.47621 3.23715 9.12004 3.23715C9.76228 3.23715 10.2833 3.7592 10.2833 4.4025C10.2833 5.0458 9.76228 5.56468 9.12004 5.56468Z" fill="#8F8F8F" />
                            </svg>
                        </span>
                    </p>
                    <div className={style.dfx_Check_CoinsContainer}>
                        {Array.from({ length: 7 }).map((_, idx) => {
                            // compute current progress within a 7-day week
                            const streakValue = streakDays;
                            const streakForWeek = streakValue > 0 ? ((streakValue - 1) % 7) + 1 : 0;
                            const active = streakValue > 0 && (idx + 1) <= streakForWeek;
                            const iconsClass = active ? `${style.dfx_Check_CoinsIcons} ${style.dfx_Check_CoinsIconsActive}` : style.dfx_Check_CoinsIcons;
                            // display actual reward based on current week multiplier (use state value for reactivity)
                            // If streak is 0, use only baseCheckinAmount (no multiplier)
                            const dayReward = coinsPerDay;
                            // display actual day number based on total streak (day 1, day 2, ... day 8, day 9, etc.)
                            // when streak is 0, show days 1-7; otherwise calculate from current week
                            const totalDayNum = streakValue === 0 ? (idx + 1) : Math.floor((streakValue - 1) / 7) * 7 + (idx + 1);
                            return (
                                <div className={style.dfx_Check_Coins} key={idx}>
                                    <div className={iconsClass}>
                                        <span className={style.dfx_Check_RoundCoin}>
                                            {/* eslint-disable-next-line @next/next/no-img-element */}
                                            <img src={idx == 6 ? '/gift.webp' : '/coinIg.webp'} alt="coinIg"
                                            />
                                        </span>
                                        <p className={style.dfx_CollectCoin}>{dayReward}</p>
                                    </div>
                                    <p className={style.dfx_CollectCoinDays}>{`Day ${totalDayNum}`}</p>
                                </div>
                            );
                        })}

                    </div>

                    <div className={style.dfx_CheckInBtn}>
                        {!user ? (
                            <button className={`${style.dfx_CheckInBtnCount} commonBtn`} disabled>
                                Login to Check In
                            </button>
                        ) : countdown ? (
                            <button className={`${style.dfx_CheckInBtnCount} commonBtn`} disabled>
                                {`Next Check In ${countdown}`}
                            </button>
                        ) : (
                            <button
                                className={`${style.dfx_CheckInBtnCount} commonBtn`}
                                onClick={handleCheckIn}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Checking In...' : 'Check In'}
                            </button>
                        )}
                    </div>
                </div>

                <div className={style.dfx_CheckCoinIcon}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src="/coinCollection.webp" alt="coinCollection" />
                </div>
            </div>
            {showInfo && <RewardRule onClose={() => setShowInfo(false)} />}
        </>
    )
}

